// Single source of truth for currency display — replaces the 3+ duplicated,
// inconsistent fmtCurrency implementations that used to live in
// CrmDashboardPage/DealsPage/DealDetailPage/OpportunitiesPage (one hardcoded ₹
// only, others hand-rolled symbol maps with a broken "other currency" fallback).
// Intl.NumberFormat already knows the correct symbol/formatting rules for any
// ISO currency code, so there's no symbol table to maintain here.
const LOCALE = 'en-IN';

// Extracts just the currency symbol (e.g. '₹', '$', '€') for use in form
// labels where a full formatted amount doesn't make sense.
export function getCurrencySymbol(currencyCode = 'INR') {
  try {
    const part = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: currencyCode })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part ? part.value : currencyCode;
  } catch (e) {
    return currencyCode;
  }
}

// Converts `amount` (in `currency`) into the base currency using a rates map
// shaped like { baseCurrency, rates: { [currency]: rateToBase } } — the same
// shape returned by GET /crm/currency/rates and used server-side by
// CurrencyService.toBase(). Falls back to 1:1 if no rate is configured yet,
// so a multi-currency total still adds up to *something* rather than NaN.
export function convertToBase(amount, currency, { baseCurrency, rates } = {}) {
  const value = Number(amount) || 0;
  if (!currency || !baseCurrency || currency === baseCurrency) return value;
  const rate = rates?.[currency];
  return typeof rate === 'number' ? value * rate : value;
}

// Converts `amount` from one currency to another via the base currency —
// rates[currency] is always "amount * rate = amount in baseCurrency", so
// converting A -> B means going A -> base -> B. Used when a record's own
// currency field is changed (e.g. a Lead/Deal edit form): the number itself
// should be re-priced to the new currency, not just relabeled, otherwise
// "$100" silently becomes "₹100" — the same digits meaning a wildly
// different amount of money.
export function convertCurrency(amount, fromCurrency, toCurrency, { baseCurrency, rates } = {}) {
  const value = Number(amount) || 0;
  if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return value;
  if (!baseCurrency || !rates) return value;

  const fromRate = fromCurrency === baseCurrency ? 1 : rates[fromCurrency];
  const toRate = toCurrency === baseCurrency ? 1 : rates[toCurrency];
  if (typeof fromRate !== 'number' || typeof toRate !== 'number' || !toRate) return value;

  const inBase = value * fromRate;
  return inBase / toRate;
}

export function formatCurrency(value, currencyCode = 'INR', { compact = false } = {}) {
  const amount = Number(value) || 0;
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: currencyCode,
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? 1 : 2,
    }).format(amount);
  } catch (e) {
    // Unknown/invalid currency code — fall back to a plain number so the UI
    // never crashes on bad data.
    return `${currencyCode} ${amount.toLocaleString(LOCALE)}`;
  }
}
